export interface WorkflowDefinition {
  wf_def_id: number;
  wf_name: string;
  description: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  trigger_type: 'schedule' | 'event' | 'manual';
  created_by: string;
  created_time: string;
  updated_time: string;
  activated_time?: string;
}

export interface DesignJSON {
  version: number;
  canvas: { scale: number; pan_x: number; pan_y: number };
  steps: DesignStep[];
  connections: any[];
}

export interface DesignStep {
  step_key: string;
  step_type_id: string;
  step_label: string;
  canvas_x: number;
  canvas_y: number;
  step_order: number;
  config: { [key: string]: any };
  // Populated only on condition (cond_if) steps. Each branch is its own
  // ordered list of child steps. Conditions are terminal in their containing
  // flow — no sibling steps are allowed after the condition; downstream work
  // continues inside the matching branch.
  yes_branch?: DesignStep[];
  no_branch?: DesignStep[];
}

export interface StepDefinition {
  id: string;
  cat: string;
  icon: string;
  bg: string;
  fg: string;
  name: string;
  desc: string;
  type: 'trigger' | 'condition' | 'action' | 'end';
  fields: string;
}

export interface NewFieldSpec {
  key?: string;
  section?: string;
  label: string;
  type?:
    | 'text'
    | 'number'
    | 'select'
    | 'textarea'
    | 'toggle'
    | 'tags'
    | 'time'
    | 'date'
    | 'assignees'
    | 'status-picker';
  opts?: string[];
  opts_api?: string;
  placeholder?: string;
  req?: boolean;
  default?: any;
  // System fields auto-filled from session storage; persisted but not rendered.
  hidden?: boolean;
  // Conditionally render the field only when another field's value matches.
  showWhen?: { key: string; equals: any };
  // Conditionally hide the field when another field's value matches — inverse
  // of showWhen, used to swap mutually-exclusive inputs (e.g. Subject/Body
  // hidden when "Choose template" toggle is on).
  hideWhen?: { key: string; equals: any };
}


export const STEP_DEFS: StepDefinition[] = [
  { id: 'trigger_schedule', cat: 'Triggers',   icon: '🕐', bg: '#E8F8F0', fg: '#2DD98F', name: 'Schedule',        desc: 'Run on a time schedule',   type: 'trigger',   fields: 'schedule' },
  { id: 'trigger_event',    cat: 'Triggers',   icon: '⚡', bg: '#E8F0FF', fg: '#4F7FFF', name: 'Event Trigger',   desc: 'On patient journey event', type: 'trigger',   fields: 'event' },
  { id: 'trigger_manual',   cat: 'Triggers',   icon: '👆', bg: '#F0E8FF', fg: '#A078FF', name: 'Manual Trigger',  desc: 'User initiates the flow',  type: 'trigger',   fields: 'manual' },
  { id: 'cond_if',          cat: 'Conditions', icon: '⑂', bg: '#FFF8E8', fg: '#FFD166', name: 'Condition (If)',  desc: 'Branch based on a rule',   type: 'condition', fields: 'condition' },
  { id: 'act_create_task',  cat: 'Tasks',      icon: '✚', bg: '#E8F0FF', fg: '#4F7FFF', name: 'Create Case',     desc: 'Create a new case',        type: 'action',    fields: 'create_task' },
  { id: 'act_email_alert',  cat: 'Alerts',     icon: '✉', bg: '#FFF0E8', fg: '#FF8C42', name: 'Email Alert',     desc: 'Send email',               type: 'action',    fields: 'email_alert' },
  { id: 'act_delay',        cat: 'Flow',       icon: '⏳', bg: '#FFF8E8', fg: '#FFD166', name: 'Delay',           desc: 'Wait before next',         type: 'action',    fields: 'delay' },
  { id: 'act_loop',         cat: 'Flow',       icon: '↺', bg: '#FFF8E8', fg: '#FFD166', name: 'Loop',            desc: 'Repeat for list',          type: 'action',    fields: 'loop' },
  { id: 'act_end',          cat: 'Flow',       icon: '■', bg: '#F0F0F0', fg: '#555A72', name: 'End',             desc: 'Terminate workflow',       type: 'end',       fields: 'end' },
];

export const FIELDS: Record<string, NewFieldSpec[]> = {
  schedule: [
    { key: 'frequency', label: 'Frequency', type: 'select', opts: ['Daily', 'Weekly', 'Monthly', 'Hourly', 'Custom'], req: true },
    { key: 'time', label: 'Run at time', type: 'time' },
    { key: 'timezone', label: 'Timezone', type: 'select', opts: ['UTC', 'EST', 'PST', 'IST', 'CET'] },
    { key: 'cron_expression', label: 'CRON (Custom)', type: 'text', placeholder: '0 7 * * *' },
    { key: 'enabled', label: 'Enabled', type: 'toggle', default: true },
  ],

  event: [
    { key: 'event_type_key', label: 'Event type', type: 'select', opts_api: 'eventTypes', req: true },
    { key: 'filter_val', label: 'Filter value', type: 'text', placeholder: 'e.g. Approved, Denied…' },
    { key: 'enabled', label: 'Enabled', type: 'toggle', default: true },
  ],

  manual: [
    { key: 'label', label: 'Button label', type: 'text', placeholder: 'Run workflow', req: true },
    { key: 'confirm', label: 'Confirmation', type: 'toggle', default: false },
  ],

  condition: [
    // { key: 'field', label: 'Field', type: 'select', opts: ['Case Status', 'Case Priority', 'Due date', 'Assignee count', 'Patient Status'], req: true },
    { key: 'field', label: 'Field', type: 'select', opts: ['Patient Status'], req: true },
    { key: 'operator', label: 'Operator', type: 'select', opts: ['equals', 'not equals', 'contains', 'greater than', 'less than', 'is empty'], req: true },
    { key: 'value', label: 'Value', type: 'status-picker', placeholder: 'Search patient status…', req: true },
    { key: 'true_label', label: 'Yes label', type: 'text', placeholder: 'Yes' },
    { key: 'false_label', label: 'No label', type: 'text', placeholder: 'No' },
  ],

  create_task: [
    { section: 'Case Details', label: 'Case Details' },
    { key: 'task_name', label: 'Case name', type: 'text', placeholder: 'Case name', req: true },
    { key: 'task_type_id', label: 'Case type', type: 'select', opts_api: 'types' },
    { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Auto-created…' },
    { key: 'task_status_id', label: 'Status', type: 'select', opts_api: 'statuses' },
    { key: 'task_priority_id', label: 'Priority', type: 'select', opts_api: 'priorities' },
    { section: 'Scheduling', label: 'Scheduling' },
    { key: 'start_date', label: 'Start date', type: 'select', opts: ['Today', 'Tomorrow', 'In 3 days', 'In 7 days'] },
    { key: 'due_days', label: 'Due in (days)', type: 'number', placeholder: '7' },
    { section: 'Assignment', label: 'Assignment' },
    { key: 'owner_id', label: 'Owner email', type: 'text', placeholder: 'Email ID', hidden: true },
    { key: 'assignees', label: 'Assignees', type: 'assignees', placeholder: 'Search user by name or email…' },
  ],

  email_alert: [
    { section: 'Recipients', label: 'Recipients' },
    { key: 'to', label: 'To', type: 'tags', placeholder: 'Add email…', req: true },
    { key: 'cc', label: 'CC', type: 'tags', placeholder: 'Add email…' },
    { section: 'Message', label: 'Message' },
    // When ON, the user picks a saved email template instead of writing
    // Subject + Body inline. Subject/Body controls are hidden via hideWhen
    // and the email_template_key select is shown via showWhen.
    { key: 'use_template', label: 'Choose template', type: 'toggle', default: false },
    { key: 'email_template_key', label: 'Email template', type: 'select', opts_api: 'emailTemplates', req: true, showWhen: { key: 'use_template', equals: true } },
    { key: 'subject', label: 'Subject', type: 'text', placeholder: 'Subject', req: true, hideWhen: { key: 'use_template', equals: true } },
    { key: 'body', label: 'Body', type: 'textarea', placeholder: 'Automated alert…', req: true, hideWhen: { key: 'use_template', equals: true } },
  ],

  delay: [
    { key: 'amount', label: 'Wait amount', type: 'number', placeholder: '1', req: true },
    { key: 'unit', label: 'Unit', type: 'select', opts: ['Minutes', 'Hours', 'Days', 'Weeks'], req: true },
    { key: 'skip_weekends', label: 'Skip weekends', type: 'toggle', default: false },
  ],

  loop: [
    { key: 'source', label: 'Loop over', type: 'select', opts: ['Case assignees', 'Case tags', 'Care team members', 'Custom list'], req: true },
    { key: 'max_iter', label: 'Max iterations', type: 'number', placeholder: '100' },
  ],

  end: [
    { key: 'status', label: 'End status', type: 'select', opts: ['Success', 'Failure', 'Cancelled'] },
    { key: 'log_message', label: 'Log message', type: 'text', placeholder: 'Workflow completed' },
  ],
};

export function getStepDef(id: string): StepDefinition | undefined {
  return STEP_DEFS.find((s) => s.id === id);
}
